/**
 * InlineFollowupQuestion - Conversational inline prompt shown after responses
 *
 * This component renders loop-owned clarification/follow-up questions.
 * Answers can be persisted as explicit facts in user memory.
 *
 * Design: Conversational/quote-like, subtle, positioned above feedback buttons.
 * Includes a "soft decline" option and hint to type custom answer.
 *
 * Props:
 * - question: { question_id, question, options, fact_template, display }
 * - onAnswer: (answer) => void
 * - onSkip: () => void
 * - isSubmitting: boolean
 */
import { useState } from 'react';

export default function InlineFollowupQuestion({
  question,
  onAnswer,
  onSkip,
  isSubmitting = false,
}) {
  const [selectedAnswer, setSelectedAnswer] = useState(null);

  if (!question) return null;

  const { question: questionText, options } = question;

  const handleAnswerClick = (answer) => {
    if (isSubmitting) return;
    setSelectedAnswer(answer);
    onAnswer(answer);
  };

  const handleSoftDecline = () => {
    if (isSubmitting) return;
    // "Not sure right now" is a soft signal - don't penalize like a skip
    // But still dismiss the question
    onSkip();
  };

  return (
    <div className="mt-3 mb-1 animate-fadeIn">
      {/* Conversational question - indented quote style */}
      <div className="pl-3 border-l-2 border-slate-200">
        <p className="text-sm text-slate-600 italic mb-2.5">"{questionText}"</p>

        {/* Option pills - subtle, inline */}
        <div className="flex flex-wrap gap-1.5 items-center">
          {options?.map((option, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handleAnswerClick(option)}
              disabled={isSubmitting}
              className={`
                px-2.5 py-1 text-xs rounded-full
                border transition-all duration-150
                ${
                  selectedAnswer === option
                    ? 'border-indigo-400 bg-indigo-50 text-indigo-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                }
                ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              {option}
            </button>
          ))}

          {/* Soft decline option */}
          <button
            type="button"
            onClick={handleSoftDecline}
            disabled={isSubmitting}
            className={`
              px-2.5 py-1 text-xs rounded-full
              transition-all duration-150
              text-slate-400 hover:text-slate-500 hover:bg-slate-50
              ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            Not sure right now
          </button>

          {/* Hint for "other" - just type in chat */}
          <span className="text-[10px] text-slate-300 ml-1">or just tell me</span>
        </div>
      </div>

      {/* Submitting indicator - minimal */}
      {isSubmitting && (
        <div className="mt-2 pl-3 flex items-center gap-1.5 text-xs text-slate-400">
          <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <span>Got it</span>
        </div>
      )}
    </div>
  );
}
